import { Box, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, type FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { BackgroundImagePreview } from 'src/pages/Settings/memberships/components/background-image-preview';
import { SectionWrapper } from 'src/pages/Settings/memberships/components/plans-tab/styled';
import {
  DEFAULT_BRANDING_VALUES,
  PlansTabField,
  type PlansTabFormValues,
} from 'src/pages/Settings/memberships/components/plans-tab/types';
import {
  isValidColor,
  normalizeHex,
} from 'src/utils/color-utils';
import { isValidHttpUrl } from 'src/utils/url';

const COLOR_FIELDS: { name: FieldPath<PlansTabFormValues>; label: string }[] = [
  { name: PlansTabField.OptionsBrandingPrimaryColor, label: 'Primary color' },
  { name: PlansTabField.OptionsBrandingSecondaryColor, label: 'Secondary color' },
  { name: PlansTabField.OptionsBrandingBannerColor, label: 'Banner color' },
  { name: PlansTabField.OptionsBrandingPageBlock1Color, label: 'Page block 1 color' },
];

const COMMIT_DELAY_MS = 300;

/** Updates local swatch while dragging; commits to form only on blur or after user stops dragging (debounced). */
const ColorPickerField = ({
  field,
  label,
  readOnly,
  hasError,
}: {
  field: { value: unknown; onChange: (value: string) => void };
  label: string;
  readOnly: boolean;
  hasError?: boolean;
}) => {
  const rawValue = (field.value as string | undefined) ?? '';
  const formHex = normalizeHex(rawValue) || '#000000';
  const [localHex, setLocalHex] = useState(rawValue.trim() || formHex);
  const commitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalHex(rawValue.trim() || formHex);
  }, [rawValue, formHex]);

  useEffect(() => {
    return () => {
      if (commitTimeoutRef.current) clearTimeout(commitTimeoutRef.current);
    };
  }, []);

  const defaultHex = '#000000';
  const commitToForm = (hex: string) => {
    if (commitTimeoutRef.current) {
      clearTimeout(commitTimeoutRef.current);
      commitTimeoutRef.current = null;
    }
    const normalized = normalizeHex(hex);
    const next = normalized || (isValidColor(formHex) ? formHex : defaultHex);
    field.onChange(next);
  };

  const scheduleCommit = (hex: string) => {
    if (commitTimeoutRef.current) clearTimeout(commitTimeoutRef.current);
    commitTimeoutRef.current = setTimeout(() => {
      commitTimeoutRef.current = null;
      commitToForm(hex);
    }, COMMIT_DELAY_MS);
  };

  const handlePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = normalizeHex(e.target.value) || '#000000';
    setLocalHex(next);
    scheduleCommit(next);
  };

  const handlePickerBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = (e.target.value || '').trim();
    const next = isValidColor(raw) ? (normalizeHex(raw) || raw) : defaultHex;
    if (commitTimeoutRef.current) {
      clearTimeout(commitTimeoutRef.current);
      commitTimeoutRef.current = null;
    }
    setLocalHex(next);
    field.onChange(next);
  };

  const handlePickerRelease = () => {
    if (commitTimeoutRef.current) {
      clearTimeout(commitTimeoutRef.current);
      commitTimeoutRef.current = null;
    }
    commitToForm(localHex);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      py={1.5}
      px={0}
      flexWrap="wrap"
    >
      <Stack direction="row" alignItems="center" gap={1.5} minWidth={0} flex={1}>
        <Box
          component="span"
          sx={{
            position: 'relative',
            flexShrink: 0,
            borderRadius: '50%',
            boxShadow: (t) =>
              `0 0 0 1px ${t.palette.divider}, 0 1px 2px ${t.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.08)'}`,
            overflow: 'hidden',
          }}
        >
          <Box
            component="input"
            type="color"
            value={isValidColor(localHex) ? localHex : defaultHex}
            disabled={readOnly}
            draggable={false}
            aria-label={`${label} color picker`}
            onChange={handlePickerChange}
            onBlur={handlePickerBlur}
            onMouseUp={handlePickerRelease}
            onPointerUp={handlePickerRelease}
            sx={{
              display: 'block',
              width: 28,
              height: 28,
              padding: 0,
              border: 'none',
              cursor: readOnly ? 'default' : 'pointer',
              backgroundColor: 'transparent',
              '&::-webkit-color-swatch-wrapper': { padding: 0 },
              '&::-webkit-color-swatch': {
                border: 'none',
                borderRadius: 4,
              },
              '&::-moz-color-swatch': {
                border: 'none',
                borderRadius: 4,
              },
            }}
          />
        </Box>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ fontSize: (t) => t.typography.pxToRem(14), color: 'text.primary' }}
        >
          {label}
        </Typography>
      </Stack>
      <TextField
        size="small"
        value={rawValue}
        disabled={readOnly}
        onChange={(e) => field.onChange(e.target.value)}
        onBlur={() => {
          const v = rawValue.trim();
          if (v && !isValidColor(v)) {
            field.onChange(defaultHex);
            setLocalHex(defaultHex);
          }
        }}
        placeholder="#000000"
        error={Boolean(hasError) || (rawValue.trim() !== '' && !isValidColor(rawValue))}
        variant="outlined"
        inputProps={{
          'aria-label': `${label} hex`,
          maxLength: 7,
        }}
        sx={{ width: 112, flexShrink: 0 }}
      />
    </Stack>
  );
};

type StyleTabProps = { readOnly: boolean };

export const StyleTab = ({ readOnly }: StyleTabProps): React.JSX.Element => {
  const { control } = useFormContext<PlansTabFormValues>();

  const backgroundImageUrl = useWatch({ control, name: PlansTabField.OptionsBrandingBackgroundImageUrl });

  const defaultColorByField = useMemo(
    () =>
      ({
        [PlansTabField.OptionsBrandingPrimaryColor]: DEFAULT_BRANDING_VALUES.primaryColor,
        [PlansTabField.OptionsBrandingSecondaryColor]: DEFAULT_BRANDING_VALUES.secondaryColor,
        [PlansTabField.OptionsBrandingBannerColor]: DEFAULT_BRANDING_VALUES.bannerColor,
        [PlansTabField.OptionsBrandingPageBlock1Color]: DEFAULT_BRANDING_VALUES.pageBlock1Color,
      }) as Record<FieldPath<PlansTabFormValues>, string>,
    [],
  );

  return (
    <Stack spacing={3} sx={{ px: 2, py: 2 }}>
      <SectionWrapper>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ fontSize: (t) => t.typography.pxToRem(14), color: 'text.primary', mb: 1 }}
        >
          Brand colors
        </Typography>

        <Stack spacing={0} sx={{ '& > *': { borderBottom: 1, borderColor: 'divider' } }}>
          {COLOR_FIELDS.map(({ name, label }) => (
            <Controller
              key={name}
              name={name}
              control={control}
              defaultValue={defaultColorByField[name]}
              rules={{
                validate: (value) => {
                  const str = typeof value === 'string' ? value : '';
                  const v = str.trim();
                  if (v === '') return 'Color is required';
                  if (!isValidColor(str)) return 'Enter a valid color (#nnn or #nnnnnn)';
                  return true;
                },
              }}
              render={({ field, fieldState }) => (
                <ColorPickerField
                  field={field}
                  label={label}
                  readOnly={readOnly}
                  hasError={Boolean(fieldState.error)}
                />
              )}
            />
          ))}
        </Stack>
      </SectionWrapper>

      <SectionWrapper>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ fontSize: (t) => t.typography.pxToRem(14), color: 'text.primary', mb: 1 }}
        >
          Background image
        </Typography>

        <Controller
          name={PlansTabField.OptionsBrandingBackgroundImageUrl}
          control={control}
          defaultValue=""
          rules={{
            validate: (value) => {
              const str = typeof value === 'string' ? value.trim() : '';
              if (str === '') return true;
              if (!isValidHttpUrl(str)) return 'Enter a valid URL (https://…)';
              return true;
            },
          }}
          render={({ field, fieldState }) => (
            <Stack spacing={1.5}>
              <TextField
                {...field}
                value={field.value ?? ''}
                disabled={readOnly}
                size="small"
                fullWidth
                placeholder="https://example.com/background.jpg"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                label="Image URL"
              />
              <BackgroundImagePreview url={typeof backgroundImageUrl === 'string' ? backgroundImageUrl : ''} />
            </Stack>
          )}
        />
      </SectionWrapper>
    </Stack>
  );
};
